import {
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  MutableRefObject,
} from "react";
import { DECIMAL_SEPARATOR } from "./constants";
import { InputState } from "./types";

export const useInputElement = (
  inputRef: MutableRefObject<HTMLInputElement | null>
): (() => HTMLInputElement | null) =>
  useCallback(() => {
    let input = inputRef.current;

    if (!input) {
      return null;
    }

    if (input.nodeName !== "INPUT") {
      input = input.querySelector("input");
    }

    if (!input) {
      throw new Error("inputComponent doesn't contain input node");
    }

    return input;
  }, [inputRef]);

type UseValueResult = {
  getValue(): string;
  getLastValue(): string;
  setValue(value: string): void;
};

const useValue = (
  inputRef: MutableRefObject<HTMLInputElement | null>,
  initialValue: string
): UseValueResult => {
  const getInputElement = useInputElement(inputRef);
  const valueRef = useRef(initialValue);

  const getValue = useCallback(() => {
    const input = getInputElement();

    if (!input) {
      return initialValue;
    }

    return input.value;
  }, [getInputElement, initialValue]);

  const getLastValue = useCallback(() => valueRef.current, []);

  const setValue = useCallback(
    (newValue: string) => {
      valueRef.current = newValue;

      const input = getInputElement();

      if (input) {
        input.value = newValue;
      }
    },
    [getInputElement]
  );

  return {
    getValue,
    getLastValue,
    setValue,
  };
};

export const useInputState = (initialValue: string) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [caretPosition, setCaretPosition] = useState(
    initialValue.includes(DECIMAL_SEPARATOR)
      ? initialValue.indexOf(DECIMAL_SEPARATOR)
      : initialValue.length
  );

  const { getLastValue, setValue, getValue } = useValue(inputRef, initialValue);
  const [changeCount, setChangeCount] = useState(0);

  const getLastInputState = (): InputState => ({
    value: getLastValue(),
    caretPosition,
  });

  const setInputState = useCallback(
    ({ value, caretPosition }: { value: string; caretPosition: number }) => {
      setValue(value);
      setCaretPosition(caretPosition);
      setChangeCount((count) => count + 1);
    },
    [setValue, setCaretPosition]
  );

  const getInputState = () => ({
    value: getValue(),
    caretPosition,
  });

  useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.setSelectionRange(caretPosition, caretPosition);
    }
  }, [changeCount]);

  return {
    inputRef,
    getLastInputState,
    setInputState,
    getInputState,
  };
};
